import { PointerTransition, TouchTransition } from 'dnd-multi-backend';
export { MultiBackend } from 'dnd-multi-backend';
import { useDragDropManager, useDrag, useDrop, useDragLayer } from 'react-dnd';
export { DndProvider } from 'react-dnd';
import React, { createContext, useState, useImperativeHandle, useContext, useEffect, useRef, useMemo, useCallback, forwardRef } from 'react';
import { HTML5Backend, getEmptyImage } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import { ResizeObserver } from '@juggle/resize-observer';
import { motion } from 'framer-motion';
import useMeasure from 'react-use-measure';

const DragControlContext = createContext({});
const initialState$1 = {
    isLock: false,
};
const DragControlProvider = (props) => {
    const [isLock, setIsLock] = useState(initialState$1.isLock);
    return (React.createElement(DragControlContext.Provider, { value: {
            isLock,
            lock: () => setIsLock(true),
            unlock: () => setIsLock(false),
        } }, props.children));
};

const PlaceholderContext = createContext({});
const initialState = {
    dropTargetId: undefined,
    index: undefined,
};
const PlaceholderProvider = (props) => {
    const [dropTargetId, setDropTargetId] = useState(initialState.dropTargetId);
    const [index, setIndex] = useState(initialState.index);
    const showPlaceholder = (dropTargetId, index) => {
        setDropTargetId(dropTargetId);
        setIndex(index);
    };
    const hidePlaceholder = () => {
        setDropTargetId(initialState.dropTargetId);
        setIndex(initialState.index);
    };
    return (React.createElement(PlaceholderContext.Provider, { value: {
            dropTargetId,
            index,
            showPlaceholder,
            hidePlaceholder,
        } }, props.children));
};

const compareItems = (a, b) => {
    if (a.text > b.text) {
        return 1;
    }
    else if (a.text < b.text) {
        return -1;
    }
    return 0;
};

const getBackendOptions = (options = {}) => {
    return {
        backends: [
            {
                id: "html5",
                backend: HTML5Backend,
                options: options.html5,
                transition: PointerTransition,
            },
            {
                id: "touch",
                backend: TouchBackend,
                options: options.touch || { enableMouseEvents: true },
                preview: true,
                transition: TouchTransition,
            },
        ],
    };
};

const getDescendants = (treeData, id) => {
    let descendants = [];
    const search = (tree, ids) => {
        const children = tree.filter((node) => ids.includes(node.parent));
        if (children.length > 0) {
            descendants = [...descendants, ...children];
            search(tree, children.map((node) => node.id));
        }
    };
    search(treeData, [id]);
    return descendants;
};

const getDestIndex = (tree, dropTargetId, index) => {
    if (index === 0) {
        return 0;
    }
    const siblings = tree.filter((node) => node.parent === dropTargetId);
    if (siblings[index]) {
        return tree.findIndex((node) => node.id === siblings[index].id);
    }
    return tree.findIndex((node) => node.id === siblings[index - 1].id) + 1;
};

const isAncestor = (tree, sourceId, targetId) => {
    if (targetId === 0) {
        return false;
    }
    const targetNode = tree.find((node) => node.id === targetId);
    if (targetNode === undefined) {
        return false;
    }
    if (targetNode.parent === sourceId) {
        return true;
    }
    return isAncestor(tree, sourceId, targetNode.parent);
};

const isNodeModel = (arg) => {
    return (typeof arg === "object" &&
        arg !== null &&
        "id" in arg &&
        "parent" in arg &&
        "text" in arg);
};

const isDroppable = (dragSource, dropTargetId, treeContext) => {
    const { tree, rootId, canDrop } = treeContext;
    if (dragSource === null) {
        // Dropability judgment of each node in the undragged state.
        // Without this process, the newly mounted node will not be able to be dropped unless it is re-rendered
        if (dropTargetId === rootId) {
            return true;
        }
        const dropTargetNode = tree.find((node) => node.id === dropTargetId);
        if (dropTargetNode && dropTargetNode.droppable) {
            return true;
        }
        return false;
    }
    else {
        const dragSourceId = isNodeModel(dragSource) ? dragSource.id : null;
        if (canDrop) {
            const result = canDrop(dragSourceId, dropTargetId);
            if (result !== undefined) {
                return result;
            }
        }
        if (dragSourceId === dropTargetId) {
            return false;
        }
        const dragSourceNode = tree.find((node) => node.id === dragSourceId);
        const dropTargetNode = tree.find((node) => node.id === dropTargetId);
        // dragSource is external node
        if (dragSourceNode === undefined || dragSourceId === null) {
            return dropTargetId === rootId || !!dropTargetNode?.droppable;
        }
        // dropTarget is root node
        if (dropTargetNode === undefined) {
            return dragSourceNode.parent !== 0;
        }
        if (dragSourceNode.parent === dropTargetId || !dropTargetNode.droppable) {
            return false;
        }
        return !isAncestor(tree, dragSourceId, dropTargetId);
    }
};

const compareYCoord = (el, pointerY) => {
    const bbox = el.getBoundingClientRect();
    const centerY = bbox.top + bbox.height / 2;
    return pointerY > centerY ? "down" : "up";
};
const getInnerIndex = (listItems, monitor) => {
    let pos = "";
    let index = 0;
    listItems.forEach((el, key) => {
        const flag = compareYCoord(el, monitor.getClientOffset()?.y || 0);
        if (pos === "") {
            pos = flag;
        }
        else if (pos !== flag) {
            pos = flag;
            index = key;
        }
        if (key === listItems.length - 1 && flag === "down") {
            index = key + 1;
        }
    });
    return index;
};
const getOuterIndex = (node, nodeEl, monitor) => {
    const parentList = nodeEl.closest('[role="list"]');
    const parentListItems = parentList?.querySelectorAll(':scope > [role="listitem"]');
    if (!parentListItems) {
        return null;
    }
    return getInnerIndex(parentListItems, monitor);
};
const getHoverPosition = (el, pointerY, context) => {
    const bbox = el.getBoundingClientRect();
    const offsetY = context.dropTargetOffset;
    if (offsetY === 0) {
        return "middle";
    }
    const upSideY = bbox.top + offsetY;
    const lowerSideY = bbox.bottom - offsetY;
    if (pointerY > lowerSideY) {
        return "lower";
    }
    else if (pointerY < upSideY) {
        return "upper";
    }
    return "middle";
};
const getDropTarget = (node, nodeEl, monitor, context) => {
    if (!nodeEl) {
        return null;
    }
    if (node === null) {
        const listItems = nodeEl.querySelectorAll(':scope > [role="listitem"]');
        return {
            id: context.rootId,
            index: getInnerIndex(listItems, monitor),
        };
    }
    const dragSource = monitor.getItem();
    const list = nodeEl.querySelector('[role="list"]');
    const hoverPosition = getHoverPosition(nodeEl, monitor.getClientOffset()?.y || 0, context);
    if (!list) {
        if (hoverPosition === "middle") {
            return {
                id: node.id,
                index: 0,
            };
        }
        if (isDroppable(dragSource, node.parent, context)) {
            const outerIndex = getOuterIndex(node, nodeEl, monitor);
            if (outerIndex === null) {
                return null;
            }
            return {
                id: node.parent,
                index: outerIndex,
            };
        }
        return null;
    }
    else {
        if (hoverPosition === "upper") {
            if (isDroppable(dragSource, node.parent, context)) {
                const outerIndex = getOuterIndex(node, nodeEl, monitor);
                if (outerIndex === null) {
                    return null;
                }
                return {
                    id: node.parent,
                    index: outerIndex,
                };
            }
            else {
                return {
                    id: node.id,
                    index: 0,
                };
            }
        }
        const listItems = list.querySelectorAll(':scope > [role="listitem"]');
        return {
            id: node.id,
            index: getInnerIndex(listItems, monitor),
        };
    }
};

const getSrcIndex = (tree, dragSourceId) => {
    return tree.findIndex((node) => node.id === dragSourceId);
};
const getModifiedIndex = (tree, dragSourceId, dropTargetId, index) => {
    const srcIndex = getSrcIndex(tree, dragSourceId);
    let destIndex = getDestIndex(tree, dropTargetId, index);
    destIndex = destIndex > srcIndex ? destIndex - 1 : destIndex;
    return [srcIndex, destIndex];
};

/** Get all parental nodes of the given node id. */
function getParents(treeData, id) {
    const parents = [];
    let node = treeData.find((el) => el.id === id);
    while (node) {
        node = treeData.find((el) => el.id === node.parent);
        if (node)
            parents.push(node);
    }
    return parents;
}

const getTreeItem = (tree, id) => tree.find((n) => n.id === id);

const hasChildNodes = (tree, nodeId) => {
    return tree.some((node) => node.parent === nodeId);
};

const mutateTree = (tree, dragSourceId, dropTargetId) => tree.map((node) => {
    if (node.id === dragSourceId) {
        return {
            ...node,
            parent: dropTargetId,
        };
    }
    return node;
});

const arrayMoveMutable = (array, fromIndex, toIndex) => {
    const startIndex = fromIndex < 0 ? array.length + fromIndex : fromIndex;
    if (startIndex >= 0 && startIndex < array.length) {
        const endIndex = toIndex < 0 ? array.length + toIndex : toIndex;
        const [item] = array.splice(fromIndex, 1);
        array.splice(endIndex, 0, item);
    }
};
const mutateTreeWithIndex = (tree, dragSourceId, dropTargetId, index) => {
    const [srcIndex, destIndex] = getModifiedIndex(tree, dragSourceId, dropTargetId, index);
    const newTree = [...tree];
    arrayMoveMutable(newTree, srcIndex, destIndex);
    return newTree.map((node) => {
        if (node.id === dragSourceId) {
            return {
                ...node,
                parent: dropTargetId,
            };
        }
        return node;
    });
};

const TreeContext = createContext({});
const TreeProvider = (props) => {
    const [openIds, { handleToggle, handleCloseAll, handleOpenAll, handleOpen, handleClose },] = useOpenIdsHelper(props.tree, props.initialOpen);
    useImperativeHandle(props.treeRef, () => ({
        open: (targetIds) => handleOpen(targetIds, props.onChangeOpen),
        close: (targetIds) => handleClose(targetIds, props.onChangeOpen),
        openAll: () => handleOpenAll(props.onChangeOpen),
        closeAll: () => handleCloseAll(props.onChangeOpen),
    }));
    const monitor = useDragDropManager().getMonitor();
    const canDropCallback = props.canDrop;
    const canDragCallback = props.canDrag;
    const value = {
        extraAcceptTypes: [],
        listComponent: "ul",
        listItemComponent: "li",
        placeholderComponent: "li",
        sort: true,
        insertDroppableFirst: true,
        enableAnimateExpand: false,
        dropTargetOffset: 0,
        initialOpen: false,
        ...props,
        openIds,
        onDrop: (dragSource, dropTargetId, placeholderIndex) => {
            // if dragSource is null,
            // it means that the drop is from the outside of the react-dnd.
            if (!dragSource) {
                const options = {
                    dropTargetId,
                    dropTarget: getTreeItem(props.tree, dropTargetId),
                    monitor,
                };
                if (props.sort === false) {
                    options.destinationIndex = getDestIndex(props.tree, dropTargetId, placeholderIndex);
                    options.relativeIndex = placeholderIndex;
                }
                props.onDrop(props.tree, options);
            }
            else {
                const options = {
                    dragSourceId: dragSource.id,
                    dropTargetId,
                    dragSource: dragSource,
                    dropTarget: getTreeItem(props.tree, dropTargetId),
                    monitor,
                };
                let tree = props.tree;
                // If the dragSource does not exist in the tree,
                // it is an external node, so add it to the tree
                if (!getTreeItem(tree, dragSource.id)) {
                    tree = [...tree, dragSource];
                }
                if (props.sort === false) {
                    const [, destIndex] = getModifiedIndex(tree, dragSource.id, dropTargetId, placeholderIndex);
                    options.destinationIndex = destIndex;
                    options.relativeIndex = placeholderIndex;
                    props.onDrop(mutateTreeWithIndex(tree, dragSource.id, dropTargetId, placeholderIndex), options);
                    return;
                }
                props.onDrop(mutateTree(tree, dragSource.id, dropTargetId), options);
            }
        },
        canDrop: canDropCallback
            ? (dragSourceId, dropTargetId) => canDropCallback(props.tree, {
                dragSourceId: dragSourceId ?? undefined,
                dropTargetId,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                dragSource: monitor.getItem(),
                dropTarget: getTreeItem(props.tree, dropTargetId),
                monitor,
            })
            : undefined,
        canDrag: canDragCallback
            ? (id) => canDragCallback(getTreeItem(props.tree, id))
            : undefined,
        onToggle: (id) => handleToggle(id, props.onChangeOpen),
    };
    return (React.createElement(TreeContext.Provider, { value: value }, props.children));
};

const Providers = (props) => (React.createElement(TreeProvider, { ...props },
    React.createElement(DragControlProvider, null,
        React.createElement(PlaceholderProvider, null, props.children))));

const useTreeContext = () => {
    const treeContext = useContext(TreeContext);
    if (!treeContext) {
        throw new Error("useTreeContext must be used under TreeProvider");
    }
    return treeContext;
};

const useContainerClassName = (parentId, isOver) => {
    const { rootId, rootProps, classes } = useTreeContext();
    let className = classes?.container || "";
    if (isOver && classes?.dropTarget) {
        className = `${className} ${classes.dropTarget}`;
    }
    if (parentId === rootId && classes?.root) {
        className = `${className} ${classes.root}`;
    }
    if (parentId === rootId && rootProps?.className) {
        className = `${className} ${rootProps.className}`;
    }
    className = className.trim();
    return className;
};

/**
 * This is a hook to allow text selection by mouse in the text input area in a node.
 * Temporarily disables node dragging while the pointer is over the text input area.
 */
const useDragControl = (ref) => {
    const dragControlContext = useContext(DragControlContext);
    useEffect(() => {
        if (!ref.current)
            return;
        const node = ref.current;
        const lock = (e) => {
            const { target } = e;
            if (target instanceof HTMLInputElement ||
                target instanceof HTMLTextAreaElement) {
                dragControlContext.lock();
            }
        };
        const unlock = (e) => {
            const { target } = e;
            if (target instanceof HTMLInputElement ||
                target instanceof HTMLTextAreaElement) {
                dragControlContext.unlock();
            }
        };
        const handleMouseOver = (e) => lock(e);
        const handleMouseOut = (e) => unlock(e);
        const handleFocusIn = (e) => lock(e);
        const handleFocusOut = (e) => unlock(e);
        // In Firefox or Safari,
        // the focusout event is not fired when the focused element is unmounted.
        // Therefore, it detects the unmounting of a child element
        // and unlocks tree if the focus is on the body element after unmounting.
        const observer = new MutationObserver(() => {
            if (document.activeElement === document.body) {
                dragControlContext.unlock();
            }
        });
        observer.observe(node, {
            subtree: true,
            childList: true,
        });
        node.addEventListener("mouseover", handleMouseOver);
        node.addEventListener("mouseout", handleMouseOut);
        node.addEventListener("focusin", handleFocusIn);
        node.addEventListener("focusout", handleFocusOut);
        return () => {
            observer.disconnect();
            node.removeEventListener("mouseover", handleMouseOver);
            node.removeEventListener("mouseout", handleMouseOut);
            node.removeEventListener("focusin", handleFocusIn);
            node.removeEventListener("focusout", handleFocusOut);
        };
    }, [ref, dragControlContext]);
    useEffect(() => {
        ref.current?.setAttribute("draggable", dragControlContext.isLock ? "false" : "true");
    }, [ref, dragControlContext.isLock]);
};

const useDragHandle = (containerRef, handleRef, drag) => {
    if (handleRef.current) {
        drag(handleRef);
    }
    else {
        drag(containerRef);
    }
    useEffect(() => {
        if (handleRef.current) {
            drag(handleRef);
        }
        else {
            drag(containerRef);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handleRef.current]);
};

const ItemTypes = {
    TREE_ITEM: Symbol(),
};

let dragSourceElement = null;
const register = (e) => {
    const { target } = e;
    if (target instanceof HTMLElement) {
        const source = target.closest('[role="listitem"]');
        if (e.currentTarget === source) {
            dragSourceElement = source;
        }
    }
};
const handleDragStart = (e) => register(e);
const handleTouchStart = (e) => register(e);
const useDragNode = (item, ref) => {
    const treeContext = useTreeContext();
    useEffect(() => {
        const node = ref.current;
        node?.addEventListener("dragstart", handleDragStart);
        node?.addEventListener("touchstart", handleTouchStart, {
            passive: true,
        });
        return () => {
            node?.removeEventListener("dragstart", handleDragStart);
            node?.removeEventListener("touchstart", handleTouchStart);
        };
    }, [ref]);
    const [{ isDragging }, drag, preview] = useDrag({
        type: ItemTypes.TREE_ITEM,
        item: (monitor) => {
            const dragItem = { ref, ...item };
            if (treeContext.onDragStart) {
                treeContext.onDragStart(dragItem, monitor);
            }
            return dragItem;
        },
        end: (item, monitor) => {
            const dragItem = item;
            if (treeContext.onDragEnd) {
                treeContext.onDragEnd(dragItem, monitor);
            }
        },
        canDrag: () => {
            const { canDrag } = treeContext;
            if (dragSourceElement !== ref.current) {
                return false;
            }
            if (canDrag) {
                return canDrag(item.id);
            }
            return true;
        },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });
    return [isDragging, drag, preview];
};

const useDragOver = (id, isOpen, dragOverHandler) => {
    const stack = useRef(0);
    const timer = useRef(0);
    const onDragEnter = () => {
        stack.current += 1;
        if (stack.current === 1 && !isOpen) {
            timer.current = window.setTimeout(() => dragOverHandler(id), 500);
        }
    };
    const onDragLeave = () => {
        stack.current -= 1;
        if (stack.current === 0) {
            window.clearTimeout(timer.current);
        }
    };
    const onDrop = () => {
        if (timer.current > 0) {
            window.clearTimeout(timer.current);
        }
        stack.current = 0;
        timer.current = 0;
    };
    return {
        onDragEnter,
        onDragLeave,
        onDrop,
    };
};

const useDropNode = (item, ref) => {
    const treeContext = useTreeContext();
    const placeholderContext = useContext(PlaceholderContext);
    const [{ isOver, dragSource }, drop] = useDrop({
        accept: [ItemTypes.TREE_ITEM, ...treeContext.extraAcceptTypes],
        drop: (dragItem, monitor) => {
            const { dropTargetId, index } = placeholderContext;
            if (monitor.isOver({ shallow: true }) &&
                dropTargetId !== undefined &&
                index !== undefined) {
                // If the drag source is outside the react-dnd,
                // NativeDragItem is passed instead of DragItem.
                treeContext.onDrop(isNodeModel(dragItem) ? dragItem : null, dropTargetId, index);
            }
            placeholderContext.hidePlaceholder();
        },
        canDrop: (dragItem, monitor) => {
            if (monitor.isOver({ shallow: true })) {
                const dropTarget = getDropTarget(item, ref.current, monitor, treeContext);
                if (dropTarget === null) {
                    return false;
                }
                return isDroppable(dragItem, dropTarget.id, treeContext);
            }
            return false;
        },
        hover: (dragItem, monitor) => {
            if (monitor.isOver({ shallow: true })) {
                const { dropTargetId, index, showPlaceholder, hidePlaceholder } = placeholderContext;
                const dropTarget = getDropTarget(item, ref.current, monitor, treeContext);
                if (dropTarget === null ||
                    !isDroppable(dragItem, dropTarget.id, treeContext)) {
                    hidePlaceholder();
                    return;
                }
                if (dropTarget.id !== dropTargetId || dropTarget.index !== index) {
                    showPlaceholder(dropTarget.id, dropTarget.index);
                }
            }
        },
        collect: (monitor) => {
            const dragSource = monitor.getItem();
            return {
                isOver: monitor.isOver({ shallow: true }) && monitor.canDrop(),
                dragSource,
            };
        },
    });
    return [isOver, dragSource, drop];
};

const useDropRoot = (ref) => {
    const treeContext = useTreeContext();
    const placeholderContext = useContext(PlaceholderContext);
    const [{ isOver, dragSource }, drop] = useDrop({
        accept: [ItemTypes.TREE_ITEM, ...treeContext.extraAcceptTypes],
        drop: (dragItem, monitor) => {
            const { rootId, onDrop } = treeContext;
            const { dropTargetId, index } = placeholderContext;
            if (monitor.isOver({ shallow: true }) &&
                dropTargetId !== undefined &&
                index !== undefined) {
                // If the drag source is outside the react-dnd,
                // NativeDragItem is passed instead of DragItem.
                onDrop(isNodeModel(dragItem) ? dragItem : null, rootId, index);
            }
            placeholderContext.hidePlaceholder();
        },
        canDrop: (dragItem, monitor) => {
            const { rootId } = treeContext;
            if (monitor.isOver({ shallow: true })) {
                if (dragItem === undefined) {
                    return false;
                }
                return isDroppable(dragItem, rootId, treeContext);
            }
            return false;
        },
        hover: (dragItem, monitor) => {
            if (monitor.isOver({ shallow: true })) {
                const { rootId } = treeContext;
                const { dropTargetId, index, showPlaceholder, hidePlaceholder } = placeholderContext;
                const dropTarget = getDropTarget(null, ref.current, monitor, treeContext);
                if (dropTarget === null ||
                    !isDroppable(dragItem, rootId, treeContext)) {
                    hidePlaceholder();
                    return;
                }
                if (dropTarget.id !== dropTargetId || dropTarget.index !== index) {
                    showPlaceholder(dropTarget.id, dropTarget.index);
                }
            }
        },
        collect: (monitor) => {
            const dragSource = monitor.getItem();
            return {
                isOver: monitor.isOver({ shallow: true }) && monitor.canDrop(),
                dragSource,
            };
        },
    });
    return [isOver, dragSource, drop];
};

const useOpenIdsHelper = (tree, initialOpen) => {
    // Only a parent node with a child node can be opened.
    // The droppable property has no effect.
    // However, if an ID is specified, It is applied unconditionally.
    const initialOpenIds = useMemo(() => {
        if (initialOpen === true) {
            return tree
                .filter((node) => hasChildNodes(tree, node.id))
                .map((node) => node.id);
        }
        else if (Array.isArray(initialOpen)) {
            return initialOpen;
        }
        return [];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialOpen]);
    const [openIds, setOpenIds] = useState(initialOpenIds);
    useEffect(() => setOpenIds(initialOpenIds), [initialOpenIds]);
    const handleToggle = (targetId, callback) => {
        const newOpenIds = openIds.includes(targetId)
            ? openIds.filter((id) => id !== targetId)
            : [...openIds, targetId];
        setOpenIds(newOpenIds);
        if (callback) {
            callback(newOpenIds);
        }
    };
    const handleCloseAll = (callback) => {
        setOpenIds([]);
        if (callback) {
            callback([]);
        }
    };
    const handleOpenAll = (callback) => {
        const newOpenIds = tree
            .filter((node) => hasChildNodes(tree, node.id))
            .map((node) => node.id);
        setOpenIds(newOpenIds);
        if (callback) {
            callback(newOpenIds);
        }
    };
    const handleOpen = (targetIds, callback) => {
        let newOpenIds = [];
        if (Array.isArray(targetIds)) {
            const targetNodes = tree.filter((node) => targetIds.includes(node.id) && hasChildNodes(tree, node.id));
            newOpenIds = [...openIds, ...targetNodes.map((node) => node.id)].filter((value, index, self) => self.indexOf(value) === index);
        }
        else {
            newOpenIds = openIds.includes(targetIds)
                ? openIds
                : [...openIds, targetIds];
        }
        setOpenIds(newOpenIds);
        if (callback) {
            callback(newOpenIds);
        }
    };
    const handleClose = (targetIds, callback) => {
        const newOpenIds = openIds.filter((id) => Array.isArray(targetIds) ? !targetIds.includes(id) : id !== targetIds);
        setOpenIds(newOpenIds);
        if (callback) {
            callback(newOpenIds);
        }
    };
    return [
        openIds,
        { handleToggle, handleCloseAll, handleOpenAll, handleOpen, handleClose },
    ];
};

const useTreeDragLayer = () => {
    return useDragLayer((monitor) => {
        const itemType = monitor.getItemType();
        return {
            item: monitor.getItem(),
            clientOffset: monitor.getClientOffset(),
            isDragging: monitor.isDragging() && itemType === ItemTypes.TREE_ITEM,
        };
    });
};

function AnimateHeight(props) {
    const { isVisible, ease = "easeIn", duration, variants = {
        open: { opacity: 1, height: "auto" },
        close: { opacity: 0, height: 0 },
    }, children, } = props;
    const [ref, { height }] = useMeasure({ polyfill: ResizeObserver });
    const [isVisibleChildren, setIsVisibleChildren] = useState(isVisible);
    const [isVisibleContainer, setIsVisibleContainer] = useState(isVisible);
    const [transition, setTransition] = useState(false);
    const onAnimationComplete = () => {
        setTransition(false);
        if (!isVisible) {
            setIsVisibleChildren(false);
        }
    };
    useEffect(() => {
        setTransition(true);
        if (isVisible) {
            setIsVisibleChildren(true);
        }
        else {
            setIsVisibleContainer(false);
        }
    }, [isVisible]);
    const isHeightZero = height === 0;
    useEffect(() => {
        if (isVisibleChildren) {
            setIsVisibleContainer(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isHeightZero]);
    return (React.createElement(motion.div, { style: transition ? { overflow: "hidden" } : undefined, onAnimationComplete: onAnimationComplete, initial: isVisibleContainer ? "open" : "close", animate: isVisibleContainer ? "open" : "close", inherit: false, variants: variants, transition: { ease, duration: computeDuration(height, duration) } },
        React.createElement("div", { ref: ref }, isVisibleChildren && children)));
}
function computeDuration(height, fixedDuration) {
    if (fixedDuration) {
        return fixedDuration;
    }
    if (!height) {
        return 0;
    }
    const constant = height / 36;
    // ??? don't know why use below computed expression (just copy it from somewhere)
    return Math.round((4 + 10 * constant ** 0.25 + constant / 5) * 10) / 1500;
}

const Node = (props) => {
    const treeContext = useTreeContext();
    const placeholderContext = useContext(PlaceholderContext);
    const containerRef = useRef(null);
    const handleRef = useRef(null);
    const item = treeContext.tree.find((node) => node.id === props.id);
    const { openIds, classes, enableAnimateExpand } = treeContext;
    const open = openIds.includes(props.id);
    const [isDragging, drag, preview] = useDragNode(item, containerRef);
    const [isOver, dragSource, drop] = useDropNode(item, containerRef);
    useDragHandle(containerRef, handleRef, drag);
    if (isDroppable(dragSource, props.id, treeContext)) {
        drop(containerRef);
    }
    useEffect(() => {
        if (treeContext.dragPreviewRender) {
            preview(getEmptyImage(), { captureDraggingState: true });
        }
        else if (handleRef.current) {
            preview(containerRef);
        }
    }, [preview, treeContext.dragPreviewRender]);
    useDragControl(containerRef);
    const handleToggle = useCallback(() => {
        treeContext.onToggle(item.id);
    }, [item.id, treeContext]);
    const Component = treeContext.listItemComponent;
    const draggable = treeContext.canDrag ? treeContext.canDrag(props.id) : true;
    const isDropTarget = placeholderContext.dropTargetId === props.id;
    const params = {
        depth: props.depth,
        isOpen: open,
        isDragging,
        isDropTarget,
        draggable,
        hasChild: hasChildNodes(treeContext.tree, props.id),
        containerRef,
        handleRef,
        onToggle: handleToggle,
    };
    let className = "";
    if (classes?.listItem) {
        className =
            typeof classes.listItem === "string"
                ? classes.listItem
                : classes.listItem(item, params);
    }
    if (isOver && classes?.dropTarget) {
        className = `${className} ${classes.dropTarget}`;
    }
    if (isDragging && classes?.draggingSource) {
        className = `${className} ${classes.draggingSource}`;
    }
    return (React.createElement(Component, { ref: containerRef, className: className, role: "listitem" },
        treeContext.render(item, params),
        enableAnimateExpand && params.hasChild && (React.createElement(AnimateHeight, { isVisible: open },
            React.createElement(Container, { parentId: props.id, depth: props.depth + 1 }))),
        !enableAnimateExpand && params.hasChild && open && (React.createElement(Container, { parentId: props.id, depth: props.depth + 1 }))));
};

const Placeholder = (props) => {
    const { placeholderRender, placeholderComponent: Component, classes, } = useTreeContext();
    const placeholderContext = useContext(PlaceholderContext);
    const manager = useDragDropManager();
    const monitor = manager.getMonitor();
    const dragSource = monitor.getItem();
    if (!placeholderRender || !dragSource) {
        return null;
    }
    const visible = props.dropTargetId === placeholderContext.dropTargetId &&
        (props.index === placeholderContext.index ||
            (props.index === undefined &&
                props.listCount === placeholderContext.index));
    const targetIds = monitor.getTargetIds();
    if (!visible || !targetIds.length) {
        return null;
    }
    return (React.createElement(Component, { className: classes?.placeholder || "" }, placeholderRender(dragSource, { depth: props.depth })));
};

const Container = (props) => {
    const treeContext = useTreeContext();
    const ref = useRef(null);
    const nodes = treeContext.tree.filter((l) => l.parent === props.parentId);
    let view = nodes;
    const sortCallback = typeof treeContext.sort === "function" ? treeContext.sort : compareItems;
    if (treeContext.insertDroppableFirst) {
        let droppableNodes = nodes.filter((n) => n.droppable);
        let nonDroppableNodes = nodes.filter((n) => !n.droppable);
        if (treeContext.sort === false) {
            view = [...droppableNodes, ...nonDroppableNodes];
        }
        else {
            droppableNodes = droppableNodes.sort(sortCallback);
            nonDroppableNodes = nonDroppableNodes.sort(sortCallback);
            view = [...droppableNodes, ...nonDroppableNodes];
        }
    }
    else {
        if (treeContext.sort !== false) {
            view = nodes.sort(sortCallback);
        }
    }
    const [isOver, dragSource, drop] = useDropRoot(ref);
    if (props.parentId === treeContext.rootId &&
        isDroppable(dragSource, treeContext.rootId, treeContext)) {
        drop(ref);
    }
    const className = useContainerClassName(props.parentId, isOver);
    const rootProps = treeContext.rootProps || {};
    const Component = treeContext.listComponent;
    return (React.createElement(Component, { ref: ref, role: "list", ...rootProps, className: className },
        view.map((node, index) => (React.createElement(React.Fragment, { key: node.id },
            React.createElement(Placeholder, { depth: props.depth, listCount: view.length, dropTargetId: props.parentId, index: index }),
            React.createElement(Node, { id: node.id, depth: props.depth })))),
        React.createElement(Placeholder, { depth: props.depth, listCount: view.length, dropTargetId: props.parentId })));
};

const rootStyle = {
    height: "100%",
    left: 0,
    pointerEvents: "none",
    position: "fixed",
    top: 0,
    width: "100%",
    zIndex: 100,
};
const getItemStyles = (monitorProps) => {
    const offset = monitorProps.clientOffset;
    if (!offset) {
        return {};
    }
    const { x, y } = offset;
    const transform = `translate(${x}px, ${y}px)`;
    return {
        pointerEvents: "none",
        transform,
    };
};
const DragLayer = () => {
    const context = useTreeContext();
    const monitorProps = useTreeDragLayer();
    const { isDragging, clientOffset } = monitorProps;
    if (!isDragging || !clientOffset) {
        return null;
    }
    return (React.createElement("div", { style: rootStyle },
        React.createElement("div", { style: getItemStyles(monitorProps) }, context.dragPreviewRender && context.dragPreviewRender(monitorProps))));
};

function TreeInner(props, ref) {
    return (React.createElement(Providers, { ...props, treeRef: ref },
        props.dragPreviewRender && React.createElement(DragLayer, null),
        React.createElement(Container, { parentId: props.rootId, depth: 0 })));
}
const Tree = forwardRef(TreeInner);

export { Container, DragLayer, ItemTypes, Node, Tree, compareItems, getBackendOptions, getDescendants, getDestIndex, getDropTarget, getModifiedIndex, getParents, getTreeItem, hasChildNodes, isAncestor, isDroppable, isNodeModel, mutateTree, mutateTreeWithIndex, useContainerClassName, useDragControl, useDragHandle, useDragNode, useDragOver, useDropNode, useDropRoot, useOpenIdsHelper, useTreeContext, useTreeDragLayer };
//# sourceMappingURL=index.js.map
